import React, { useState, useRef, useCallback } from 'react';
import { Textinput, Typo, Button, Flex, SearchSelect, SubPage, Loading, theme } from '../../../ui';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useEditableCourses } from '../../courses/hooks';
import { useTranslation } from 'react-i18next';
import { useExportsActions } from '../hooks/useExportsActions';
import { CONTENT_SMALL } from '../../../utils/media';
import PageControls from '../../../ui/Page/PageControls';

const CourseExports = () => {
  const { t } = useTranslation();

  const { sendExport, userExport } = useExportsActions();

  const exportTypes = [
    { name: t('export.personalInfo'), id: 'users-info' },
    { name: t('export.quizQuestion'), id: 5 },
    { name: t('export.taskStatistics'), id: 3 },
    { name: t('export.quizOverview'), id: 2 },
    { name: t('export.progression'), id: 1 },
  ];
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedExport, setSelectedExport] = useState(null);
  const emailRef = useRef(null);
  const [isXlsx, setIsXlsx] = useState(false);

  const {
    courses = [],
    isLoading,
    error,
  } = useEditableCourses({
    searchText: '',
    showUncorrected: false,
  });

  const validCourses = courses?.filter((course) => course && course.id && course.name) || [];

  const clearData = useCallback(() => {
    setSelectedExport(null);
    setSelectedCourse(null);
    setIsXlsx(false);
  }, []);

  const handleSend = useCallback(async () => {
    if (!selectedExport || !selectedCourse) {
      return;
    }

    if (selectedExport.id !== 'users-info') {
      const exportData = {
        exportId: selectedExport.id,
        outputFormat: isXlsx ? 'xlsx' : 'csv',
        params: { courseId: selectedCourse.id },
        recipientEmail: emailRef.current?.value || '',
      };
      await sendExport({ exportData });
      clearData();
    } else {
      const exportData = {
        courseIds: [selectedCourse.id],
        recipientEmail: emailRef.current?.value || '',
      };
      await userExport({ exportData });
    }
  }, [clearData, sendExport, userExport, isXlsx, selectedCourse, selectedExport]);

  return (
    <SubPage size={CONTENT_SMALL}>
      <Flex width={'100%'} gap={40} direction={'column'}>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <Typo.MediumText style={{ color: theme.colors.ERROR_RED }}>
            {t('export.errorLoadingCourses', 'Error loading courses. Please try again.')}
          </Typo.MediumText>
        ) : (
          <SearchSelect options={validCourses} selectedOptions={selectedCourse} label={t('globals.course')} setValue={setSelectedCourse} />
        )}
      </Flex>
      <Flex width={'100%'} gap={5} direction={'column'}>
        <SearchSelect options={exportTypes} selectedOptions={selectedExport} label={t('export.exportType')} setValue={setSelectedExport} />
        <Typo.MediumText>{t('export.customExport')}</Typo.MediumText>
        <Textinput label={t('globals.email')} propName={'text'} innerRef={(ref) => (emailRef.current = ref)} />
      </Flex>
      <FormControlLabel
        control={<Checkbox checked={isXlsx} onChange={() => setIsXlsx(!isXlsx)} />}
        label={<Typo.MediumText>{t('export.xlsFormat')}</Typo.MediumText>}
      />
      <PageControls
        openModelButton={
          <Button onClick={handleSend} disabled={!selectedExport || !selectedCourse}>
            {t('globals.send')}
          </Button>
        }
      />
    </SubPage>
  );
};

export default CourseExports;
